import { Component, OnInit, OnDestroy, OnChanges, HostListener, Input, Output, EventEmitter } from '@angular/core';
import { RouterModule, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { MenuItem, SIDEBAR_MENU } from '../layoutConstant';
import { CommonModule } from '@angular/common';
import { LucideAngularModule, ChevronUp, X } from 'lucide-angular';
import { LoginService } from '../../shared/services/login/login.service';
import { AuthService } from '../../shared/services/auth/auth.service';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-mobile-menu',
  imports: [RouterModule, CommonModule, LucideAngularModule],
  templateUrl: './mobile-menu.html',
  styleUrl: './mobile-menu.scss',
  standalone: true,
})
export class MobileMenu implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen = false;
  @Output() closeMenu = new EventEmitter<void>();

  menuItems: MenuItem[] = SIDEBAR_MENU;
  expandedMenuId: string | null = null;
  readonly ChevronUp = ChevronUp;
  readonly X = X;
  router: Router;
  private routerSubscription?: Subscription;

  // Separate top and bottom menu items
  get topMenuItems(): MenuItem[] {
    const separatorIndex = this.menuItems.findIndex((item) => item.isSeparator);
    return separatorIndex >= 0 ? this.menuItems.slice(0, separatorIndex) : this.menuItems;
  }

  get bottomMenuItems(): MenuItem[] {
    const separatorIndex = this.menuItems.findIndex((item) => item.isSeparator);
    return separatorIndex >= 0 ? this.menuItems.slice(separatorIndex) : [];
  }

  constructor(
    router: Router,
    private route: ActivatedRoute,
    private loginService: LoginService,
    private authService: AuthService,
  ) {
    this.router = router;
  }

  ngOnInit(): void {
    this.updateExpandedState();

    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateExpandedState();
        // Close menu after navigation on mobile
        if (this.isOpen) {
          this.closeMenu.emit();
        }
      });
  }

  ngOnChanges(): void {
    if (this.isOpen) {
      this.updateExpandedState();
    }
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private updateExpandedState(): void {
    // Performance no longer has expandable sub-menu items.
  }

  toggleSubMenu(menuId: string): void {
    if (this.expandedMenuId === menuId) {
      this.expandedMenuId = null;
    } else {
      this.expandedMenuId = menuId;
    }
  }

  isExpanded(menuId: string): boolean {
    return this.expandedMenuId === menuId;
  }

  isActive(menuId: string): boolean {
    if (menuId === 'performance-stats') {
      return (
        this.router.url === '/performance-stats' ||
        this.router.url.startsWith('/performance-stats') ||
        this.router.url.startsWith('/final-summary-performance') ||
        this.router.url.startsWith('/cumulative-performances')
      );
    }
    if (menuId === 'broker-settings') {
      return (
        this.router.url === '/broker-settings' ||
        this.router.url.startsWith('/broker-settings/') ||
        this.router.url === '/add-broker' ||
        this.router.url.startsWith('/add-broker/')
      );
    }
    return this.router.url === `/${menuId}` || this.router.url.startsWith(`/${menuId}/`);
  }

  isSubTabActive(subTabId: string): boolean {
    if (!this.router.url.startsWith('/performance-stats')) {
      return false;
    }

    const queryParams = this.route.snapshot.queryParams;
    const currentTab = queryParams['tab'];

    if (!currentTab) {
      return subTabId === 'profit-loss';
    }

    return currentTab === subTabId;
  }

  handleMenuClick(menuItem: MenuItem, subMenuItem?: { name: string; id: string }): void {
    if (!menuItem.id) return;

    if (menuItem.id === 'logout') {
      this.authService.logout();
      this.loginService.loginStateChange(false);
      this.router.navigate(['/login'], { replaceUrl: true });
      this.closeMenu.emit();
      return;
    }

    if (subMenuItem) {
      if (subMenuItem.id === 'profit-loss') {
        this.router.navigate(['/performance-stats'], { queryParams: { tab: 'profit-loss' } });
      } else if (subMenuItem.id === 'historical-perf') {
        this.router.navigate(['/performance-stats'], { queryParams: { tab: 'historical-perf' } });
      } else if (subMenuItem.id === 'cumulative') {
        this.router.navigate(['/performance-stats'], { queryParams: { tab: 'cumulative' } });
      } else if (subMenuItem.id === 'final-summary') {
        this.router.navigate(['/performance-stats'], {
          queryParams: { tab: 'final-summary' },
        });
      }
      this.closeMenu.emit();
    } else if (menuItem.id === 'performance-stats') {
      this.router.navigate(['/performance-stats'], { queryParams: { tab: 'cumulative' } });
      this.closeMenu.emit();
    } else {
      this.router.navigate([`/${menuItem.id}`]);
      this.closeMenu.emit();
    }
  }

  onCloseClick(): void {
    this.closeMenu.emit();
  }

  @HostListener('document:keydown', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.isOpen) {
      this.closeMenu.emit();
    }
  }
}

